import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NgClass } from '@angular/common';
import { ColumnDef, DataTableComponent } from '../../../shared/components/data-table/data-table';
import { Category, ClassificationType } from '../../../shared/models/category.models';
import { Taxonomy } from '../../../shared/models/taxonomy.models';
import { CategoryService } from '../../../core/services/category.service';
import { TaxonomyService } from '../../../core/services/taxonomy.service';
import { ToastService } from '../../../core/services/toast.service';
import { CreateCategoryModalComponent } from './create-category-modal';
import { CreateTaxonomyModalComponent } from './create-taxonomy-modal';
import { EditCategoryModalComponent } from './edit-category-modal';
import { apiErrorMessage } from '../../../shared/utils/api-error';

type ActiveTab = 'categories' | 'taxonomies';

@Component({
  selector: 'app-categories',
  imports: [
    NgClass,
    DataTableComponent,
    CreateCategoryModalComponent,
    CreateTaxonomyModalComponent,
    EditCategoryModalComponent,
  ],
  templateUrl: './categories.html',
})
export class CategoriesComponent implements OnInit {
  private categoryService = inject(CategoryService);
  private taxonomyService = inject(TaxonomyService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  protected activeTab = signal<ActiveTab>('categories');
  protected readonly ClassificationType = ClassificationType;
  protected searchQuery = signal('');
  protected pageIndex = signal(0);
  protected loading = signal(false);
  protected showCreateModal = signal(false);
  protected showCreateTaxonomyModal = signal(false);
  protected editingCategory = signal<Category | null>(null);
  protected readonly pageSize = 10;

  protected categories = signal<Category[]>([]);
  protected totalCat = signal(0);

  protected taxonomies = signal<Taxonomy[]>([]);
  protected totalTax = signal(0);

  private nameTpl = viewChild.required<TemplateRef<{ $implicit: Category }>>('nameTpl');
  private typeTpl = viewChild.required<TemplateRef<{ $implicit: Category }>>('typeTpl');
  private taxNameTpl = viewChild.required<TemplateRef<{ $implicit: Taxonomy }>>('taxNameTpl');
  private taxTypeTpl = viewChild.required<TemplateRef<{ $implicit: Taxonomy }>>('taxTypeTpl');
  private electionTypeTpl =
    viewChild.required<TemplateRef<{ $implicit: Taxonomy }>>('electionTypeTpl');
  private assignedCatTpl =
    viewChild.required<TemplateRef<{ $implicit: Taxonomy }>>('assignedCatTpl');

  protected categoryColumns = computed<ColumnDef<Category>[]>(() => [
    { key: 'categoryName', header: 'Category Name', cellTemplate: this.nameTpl() },
    { key: 'classificationType', header: 'Classification Type', cellTemplate: this.typeTpl() },
  ]);

  protected taxonomyColumns = computed<ColumnDef<Taxonomy>[]>(() => [
    { key: 'taxonomyName', header: 'Taxonomy Name', cellTemplate: this.taxNameTpl() },
    { key: 'classificationType', header: 'Classification Type', cellTemplate: this.taxTypeTpl() },
    { key: 'isMultiItem', header: 'Election Type', cellTemplate: this.electionTypeTpl() },
    {
      key: 'assignedCategories',
      header: 'Assigned Categories',
      cellTemplate: this.assignedCatTpl(),
    },
  ]);

  ngOnInit(): void {
    const tab = this.route.snapshot.queryParamMap.get('tab') as ActiveTab | null;
    if (tab) this.activeTab.set(tab);
    this.load();
  }

  protected selectTab(tab: ActiveTab): void {
    this.activeTab.set(tab);
    this.searchQuery.set('');
    this.pageIndex.set(0);
    this.load();
  }

  protected load(): void {
    if (this.activeTab() === 'categories') {
      this.loading.set(true);
      this.categoryService
        .getCategories({
          pageSize: this.pageSize,
          pageIndex: this.pageIndex(),
          filter: this.searchQuery(),
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.categories.set(res.categories);
            this.totalCat.set(res.total);
            this.loading.set(false);
          },
          error: () => this.loading.set(false),
        });
    } else {
      this.loading.set(true);
      this.taxonomyService
        .getTaxonomies({
          pageSize: this.pageSize,
          pageIndex: this.pageIndex(),
          filter: this.searchQuery(),
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.taxonomies.set(res.taxonomies);
            this.totalTax.set(res.total);
            this.loading.set(false);
          },
          error: () => this.loading.set(false),
        });
    }
  }

  protected onPageChange(page: number): void {
    this.pageIndex.set(page - 1);
    this.load();
  }

  protected onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.pageIndex.set(0);
    this.load();
  }

  protected onRowAction(row: Category | Taxonomy): void {
    if (this.activeTab() === 'categories') {
      this.editingCategory.set(row as Category);
    } else {
      const tax = row as Taxonomy;
      const type = (
        tax.classificationType === ClassificationType.Situation ? 'situation' : 'response'
      ) as 'situation' | 'response';
      this.router.navigate(['/configuration/taxonomy', type, tax.id]);
    }
  }

  protected onRowDelete(row: Category | Taxonomy): void {
    if (this.activeTab() !== 'categories') return;
    const cat = row as Category;
    const request$ =
      cat.classificationType === ClassificationType.Situation
        ? this.categoryService.deleteSituationCategory(cat.id)
        : this.categoryService.deleteResponseCategory(cat.id);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show('Category deleted successfully');
        this.load();
      },
      error: (err) =>
        this.toastService.show(apiErrorMessage(err, 'Failed to delete category'), 'error'),
    });
  }
}
